/**
 * Sit & Go, in the lobby and on the table.
 *
 * A Sit & Go is sold on three numbers — what it costs, what it pays, and how
 * many seats are left — so those are the three the card leads with. The rest is
 * detail that can wait until someone is deciding between two games rather than
 * deciding whether to play at all.
 */

import type { TournamentListing, TournamentTableView } from '@pf/protocol';

const SPEED_LABEL: Record<TournamentListing['speed'], string> = {
  regular: 'Regular',
  turbo: 'Turbo',
};

export function SngList({
  tournaments,
  walletBalance,
  onRegister,
  onUnregister,
  onOpenTable,
}: {
  tournaments: readonly TournamentListing[];
  walletBalance: number;
  onRegister: (tournamentId: string) => void;
  onUnregister: (tournamentId: string) => void;
  onOpenTable: (tableId: string) => void;
}) {
  if (tournaments.length === 0) return null;

  return (
    <div className="sng-list">
      {tournaments.map((game) => (
        <SngCard
          key={game.tournamentId}
          game={game}
          walletBalance={walletBalance}
          onRegister={onRegister}
          onUnregister={onUnregister}
          onOpenTable={onOpenTable}
        />
      ))}
    </div>
  );
}

function SngCard({
  game,
  walletBalance,
  onRegister,
  onUnregister,
  onOpenTable,
}: {
  game: TournamentListing;
  walletBalance: number;
  onRegister: (tournamentId: string) => void;
  onUnregister: (tournamentId: string) => void;
  onOpenTable: (tableId: string) => void;
}) {
  const seatsLeft = game.seats - game.registered;
  const tooPoor = walletBalance < game.buyIn;

  return (
    <div className={`sng-card${game.youAreIn ? ' in' : ''}`}>
      <div className="sng-head">
        <span className="sng-name">{game.name}</span>
        {/* A field bigger than one table is a different game, and a player
            deciding whether to sit down for two hours rather than twenty
            minutes should not have to work that out from the seat count. */}
        {game.seats > game.seatsPerTable && (
          <span className="sng-speed lat-ui">
            {Math.ceil(game.seats / game.seatsPerTable)} tables
          </span>
        )}
        <span className="sng-speed lat-ui">{SPEED_LABEL[game.speed]}</span>
      </div>

      <div className="sng-figures">
        <Figure label="Buy-in" value={game.buyIn.toLocaleString('en-US')} />
        <Figure label="Prize Pool" value={game.prizePool.toLocaleString('en-US')} accent />
        {/* Chips, not big blinds: the big blind changes every few minutes, so a
            BB count here would be true for one level and misleading after. */}
        <Figure label="Stack" value={game.startingStack.toLocaleString('en-US')} />
      </div>

      {/* Seats sold, as a bar. A number tells you the state; a bar tells you how
          close it is, which is the thing that makes someone take the last seat. */}
      <div className="sng-seats">
        {/* One pip per seat is unreadable past a table's worth, so a bigger
            field gets a bar instead of pips. */}
        <div className="sng-bar">
          {game.seats <= 10 ? (
            Array.from({ length: game.seats }, (_, index) => (
              <span key={index} className={index < game.registered ? 'on' : ''} />
            ))
          ) : (
            <span
              className="fill"
              style={{ width: `${Math.round((game.registered / game.seats) * 100)}%` }}
            />
          )}
        </div>
        <span className="sng-count lat-ui">
          {game.registered}/{game.seats}
        </span>
      </div>

      {game.status === 'registering' && (
        <p className="sng-note">
          {seatsLeft === 1
            ? 'یک صندلی مانده — با پر شدن، بازی شروع می‌شود.'
            : `${seatsLeft} صندلی مانده.`}{' '}
          پرداختی: {game.payouts.map((amount) => amount.toLocaleString('en-US')).join(' · ')}
        </p>
      )}

      <div className="sng-actions">
        {game.status === 'registering' && !game.youAreIn && (
          <button
            className="btn-check small"
            disabled={tooPoor}
            onClick={() => onRegister(game.tournamentId)}
          >
            {tooPoor ? 'Not Enough Chips' : 'Register'}
          </button>
        )}
        {game.status === 'registering' && game.youAreIn && (
          <>
            <span className="sng-state">ثبت‌نام شدید — منتظر بقیه.</span>
            <button className="btn-plain small" onClick={() => onUnregister(game.tournamentId)}>
              Unregister
            </button>
          </>
        )}
        {game.status === 'running' && game.tableId !== null && (
          <button className="btn-raise small" onClick={() => onOpenTable(game.tableId as string)}>
            {game.youAreIn ? 'Take Your Seat' : 'Watch'}
          </button>
        )}
        {game.status === 'finished' && <span className="sng-state">تمام شد.</span>}
      </div>
    </div>
  );
}

function Figure({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="figure">
      <span className="f-label lat-ui">{label}</span>
      <span className={`f-value lat-ui${accent === true ? ' accent' : ''}`}>{value}</span>
    </div>
  );
}

/**
 * The tournament strip above the table.
 *
 * Level, clock, survivors and the money — the four things a tournament player
 * checks between hands, and the four a cash player never has to think about.
 */
export function SngBar({ view }: { view: TournamentTableView }) {
  return (
    <div className="sng-bar-strip">
      <span className="chip">
        <span className="k lat-ui">Level</span>
        <span className="v lat-ui">{view.level}</span>
      </span>
      {/* Only once there is one. A row of zeroes for the first three levels is
          three levels of telling the player nothing. */}
      {view.ante > 0 && (
        <span className="chip">
          <span className="k lat-ui">Ante</span>
          <span className="v lat-ui">{view.ante.toLocaleString('en-US')}</span>
        </span>
      )}
      <span className="chip">
        <span className="k lat-ui">Next</span>
        <span className="v lat-ui">
          {view.nextLevelInMs === null ? '—' : formatClock(view.nextLevelInMs)}
        </span>
      </span>
      <span className="chip">
        <span className="k lat-ui">Left</span>
        <span className="v lat-ui">
          {view.remaining}/{view.entrants}
        </span>
      </span>
      {view.tables > 1 && (
        <span className="chip">
          <span className="k lat-ui">Tables</span>
          <span className="v lat-ui">{view.tables}</span>
        </span>
      )}
      {/* Said out loud, or the game appears to stop for no reason at the tensest
          moment of the tournament. */}
      {view.handForHand && (
        <span className="chip bubble">
          <span className="v lat-ui">HAND FOR HAND — BUBBLE</span>
        </span>
      )}
      <span className="chip">
        <span className="k lat-ui">Prizes</span>
        <span className="v lat-ui accent">
          {view.payouts.map((amount) => amount.toLocaleString('en-US')).join(' · ')}
        </span>
      </span>
    </div>
  );
}

/** Places already decided, best first. Empty until somebody busts. */
export function SngStandings({ view }: { view: TournamentTableView }) {
  if (view.finished.length === 0) return null;
  return (
    <div className="sng-standings">
      <div className="feed-head lat-ui">Finished</div>
      {view.finished.map((row) => (
        <div key={`${row.place}-${row.name}`} className="standing">
          <span className="place lat-ui">{row.place}</span>
          <span className="who">{row.name}</span>
          <span className="prize lat-ui">
            {row.prize > 0 ? row.prize.toLocaleString('en-US') : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
